/**
 * ============================================================================
 * EventPassReady.js — /events/ready, the Next Steps page (Sept 30, 2026)
 * ============================================================================
 * Where an Event Pass buyer lands after Stripe (EventPassStart.js sends him), and
 * where the dashboard's "📋 Event checklist" button brings him back later.
 *
 *   ?event_pass=success&session_id=…  confirm with the server (it asks Stripe, so
 *                                       he never waits on the webhook), then show
 *                                       the pass and the steps.
 *   ?event_pass=cancelled               the account is saved but unpaid: offer the
 *                                       payment again (the pass kinds this account
 *                                       may buy, from GET /event-pass).
 *   (no params)                         the checklist for the signed-in account.
 *
 * The steps start with the course (nothing else works without one) and end at
 * final results; each says where to click. Steps 1 and 3 tick themselves once the
 * account has a course / an event. The guide "Setting Up Your Tournament" has the
 * same steps in detail and hands over to the format guides.
 * ============================================================================
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StepDots } from './EventPassStart';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
const GUIDE = '/guides/how-to-run-a-golf-tournament-with-tee-time-organizer';
const green = '#2d6a4f';
const LIVE = ['available', 'active', 'read_only'];

const KIND_TEXT = {
  standard_72: { title: 'Up to 72 golfers', sub: 'Most outings and member-guests' },
  standard_250: { title: 'Up to 250 golfers', sub: 'Full-field shotguns and big days' },
  subscriber: { title: 'Up to 250 golfers', sub: 'Your plan’s subscriber price' },
};

const token = () => {
  try { return sessionStorage.getItem('impersonationToken') || localStorage.getItem('accessToken'); } catch (e) { return null; }
};
const request = async (path, method = 'GET', body) => {
  const headers = { 'Content-Type': 'application/json' };
  const t = token();
  if (t) headers.Authorization = `Bearer ${t}`;
  const r = await fetch(`${API_URL}${path}`, { method, headers, credentials: 'include', body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(d.error || `The server answered ${r.status}.`); e.status = r.status; throw e; }
  return d;
};
const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(0)}`;
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch (e) { return ''; } };
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') window.gtag('event', eventName, params);
};

// The steps. `where` is the click path, `body` what to do and why.
const STEPS = [
  {
    key: 'course',
    title: 'Add your golf course',
    where: '⚙️ Admin → Courses → + Add Course',
    body: 'Search more than 25,000 courses and import yours. Check the tees your field will play: rating, slope, par and the hole handicaps (📷 Import from Scorecard reads them from a photo). Handicaps and scorecards come from these.',
  },
  {
    key: 'settings',
    title: 'Check your settings',
    where: '🏢 Settings → Location & Time Zone',
    body: 'So times, reminders and the weather are right for the course. Organizer Contact sets where golfers’ replies go.',
  },
  {
    key: 'event',
    title: 'Create your event',
    where: 'Dashboard → + New tee time → tick “This is an event”',
    body: 'Pick the course, date and time; Slots is the size of your field. Choose Shotgun or Tee Times. Tick Private Tee Time if it’s invitation only. Then name the event in the Set up event window. Your pass attaches to it.',
  },
  {
    key: 'field',
    title: 'Bring in your golfers',
    where: '⚙️ Admin → Players → 📥 Import players',
    body: 'Have the field in a spreadsheet? Import it as Guests and put them straight on your event. Or share the invite link (🎟️ Invite on the tee time card) and golfers sign themselves up. Guests are free and don’t count toward your plan. For a net event, each golfer needs a Handicap Index and a preferred tee.',
  },
  {
    key: 'format',
    title: 'Set up teams and your game',
    where: 'Pairings, then 🎮 Games',
    body: 'Your format’s guide takes it from here: the shotgun starting holes, the teams, the game (Captain’s Choice, Best Ball, Shamble or Stroke Play), team codes and ties.',
    link: { href: `${GUIDE}#step-5`, label: 'Pick your format’s guide' },
  },
  {
    key: 'print',
    title: 'Print and play',
    where: '🖨️ Print',
    body: 'Scorecards and cart signs with each team’s starting hole. On the day, golfers score on their phones and the live leaderboard runs on every phone and the clubhouse TV (📺 TV on the tee time card).',
  },
  {
    key: 'finalize',
    title: 'Finalize the results',
    where: '🎮 Games → 🏁 Finalize Round',
    body: 'Check the cards, settle any ties and finalize. That uses your pass; results and the leaderboard stay up for 30 days.',
  },
];

function Shell({ children, wide = false }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'linear-gradient(135deg, #2d6a4f, #1b4332)', padding: '24px 16px', boxSizing: 'border-box' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: wide ? 640 : 500, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );
}

const primaryBtn = { display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center', padding: '14px 16px', borderRadius: 10, border: 'none', background: green, color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', textDecoration: 'none' };
const outlineBtn = { ...primaryBtn, background: '#fff', color: green, border: `1.5px solid ${green}` };

export default function EventPassReady() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading');   // loading | ready | unpaid | login | error
  const [pass, setPass] = useState(null);
  const [info, setInfo] = useState(null);           // { access, offer }
  const [done, setDone] = useState({});             // step key -> true
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [returned, setReturned] = useState('');     // 'success' | 'cancelled' | ''

  useEffect(() => {
    if (!token()) { setPhase('login'); return; }
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { params = new URLSearchParams(); }
    const state = params.get('event_pass') || '';
    const sessionId = params.get('session_id');
    setReturned(state);
    try { window.history.replaceState(null, '', '/events/ready'); } catch (e) { /* ignore */ }

    (async () => {
      try {
        let confirmed = null;
        if (state === 'success' && sessionId) {
          try {
            const d = await request(`/event-pass/session/${encodeURIComponent(sessionId)}`);
            confirmed = d.pass || null;
            trackEvent('event_pass_purchased', { door: 'start', kind: confirmed && confirmed.kind });
          } catch (e) { /* fall through to the access list */ }
        }
        const d = await request('/event-pass');
        setInfo(d);
        const access = d.access || {};
        const live = (access.passes || []).filter(p => LIVE.includes(p.status));
        const shown = (confirmed && LIVE.includes(confirmed.status) ? confirmed : null) || live.find(p => p.status === 'active') || live[0] || null;
        setPass(shown);
        if (!shown && !access.unlimited && !access.allowance) {
          setPhase(state === 'success' ? 'pending' : 'unpaid');
        } else {
          setPhase('ready');
        }
        // Tick the steps we can see for ourselves.
        const ticks = {};
        try { const c = await request('/courses'); if (Array.isArray(c) ? c.length : (c.courses || []).length) ticks.course = true; } catch (e) { /* ignore */ }
        try { const ev = await request('/events'); if ((ev.events || []).length) ticks.event = true; } catch (e) { /* ignore */ }
        setDone(ticks);
      } catch (e) {
        if (e.status === 401) setPhase('login');
        else { setError(e.message); setPhase('error'); }
      }
    })();
  }, []);

  const buy = async (kind) => {
    setBusy(kind); setError('');
    try {
      const d = await request('/event-pass/checkout', 'POST', { kind, return_path: '/events/ready' });
      trackEvent('event_pass_checkout_open', { kind, door: 'ready' });
      if (d.url) window.location.assign(d.url);
    } catch (e) { setError(e.message); setBusy(''); }
  };

  if (phase === 'login') {
    return (
      <Shell>
        <div style={{ display: 'grid', gap: 14, textAlign: 'center' }} data-testid="ready-login">
          <div style={{ fontSize: 30 }}>📋</div>
          <h1 style={{ fontSize: 21, fontWeight: 800, color: '#1f2937', margin: 0 }}>Your event checklist</h1>
          <p style={{ fontSize: 15, color: '#374151', margin: 0 }}>Log in to see your Event Pass and your next steps.</p>
          <button type="button" style={primaryBtn} onClick={() => navigate('/login', { state: { from: { pathname: '/events/ready', search: '' } } })}>Log in</button>
        </div>
      </Shell>
    );
  }

  if (phase === 'loading') {
    return <Shell><div style={{ textAlign: 'center', color: '#374151', fontSize: 15, padding: 20 }}>{returned === 'success' ? 'Confirming your payment…' : 'Loading your event…'}</div></Shell>;
  }

  if (phase === 'error') {
    return (
      <Shell>
        <div style={{ display: 'grid', gap: 12, textAlign: 'center' }}>
          <p style={{ color: '#b91c1c', margin: 0 }}>{error || 'Something went wrong.'}</p>
          <button type="button" style={primaryBtn} onClick={() => window.location.reload()}>Try again</button>
          <a href="/dashboard" style={{ color: green }}>Go to my dashboard</a>
        </div>
      </Shell>
    );
  }

  if (phase === 'pending') {
    return (
      <Shell>
        <StepDots at={2} />
        <div style={{ display: 'grid', gap: 12, textAlign: 'center' }} data-testid="ready-pending">
          <h1 style={{ fontSize: 21, fontWeight: 800, color: '#1f2937', margin: 0 }}>Your payment went through</h1>
          <p style={{ fontSize: 15, color: '#374151', margin: 0, lineHeight: 1.55 }}>We&rsquo;re still recording your Event Pass. This usually takes a few seconds.</p>
          <button type="button" style={primaryBtn} onClick={() => window.location.reload()}>Check again</button>
        </div>
      </Shell>
    );
  }

  if (phase === 'unpaid') {
    const kinds = (info && info.offer && info.offer.kinds) || [];
    let preferred = null;
    try { preferred = localStorage.getItem('tto_event_pass_kind'); } catch (e) { /* ignore */ }
    const ordered = kinds.slice().sort((a, b) => (b.kind === preferred) - (a.kind === preferred));
    return (
      <Shell>
        <StepDots at={2} />
        <div style={{ display: 'grid', gap: 14 }} data-testid="ready-unpaid">
          <h1 style={{ fontSize: 21, fontWeight: 800, color: '#1f2937', margin: 0, textAlign: 'center' }}>Your account is saved</h1>
          <p style={{ fontSize: 15, color: '#374151', margin: 0, lineHeight: 1.55, textAlign: 'center' }}>
            Your Event Pass isn&rsquo;t paid for yet. Choose it below to finish, then we&rsquo;ll walk you through setting up your event.
          </p>
          {ordered.map((k) => (
            <div key={k.kind} style={{ border: k.kind === preferred ? `2px solid ${green}` : '1px solid #d1d5db', borderRadius: 12, padding: '13px 15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{(KIND_TEXT[k.kind] || {}).title || k.label} · {money(k.price_cents)}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{(KIND_TEXT[k.kind] || {}).sub || ''}</div>
              </div>
              <button type="button" disabled={!!busy || !k.configured} onClick={() => buy(k.kind)} data-testid={`ready-buy-${k.kind}`}
                style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: green, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                {busy === k.kind ? 'Opening checkout…' : `Pay ${money(k.price_cents)}`}
              </button>
            </div>
          ))}
          {error && <div style={{ color: '#b91c1c', fontSize: 14 }}>{error}</div>}
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0, textAlign: 'center', lineHeight: 1.5 }}>
            One-time payment, no subscription. You can also buy it later from your dashboard.{' '}
            <a href="/dashboard" style={{ color: green }}>Go to my dashboard</a>
          </p>
        </div>
      </Shell>
    );
  }

  // phase === 'ready'
  const access = (info && info.access) || {};
  const firstOpen = STEPS.findIndex(s => !done[s.key]);
  return (
    <Shell wide>
      {returned === 'success' && <StepDots at={3} />}
      <div data-testid="ready-steps">
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', color: '#14532d', marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>
            {returned === 'success' ? '✅ You’re all set. Your Event Pass is ready.' : '🎟️ Your Event Pass'}
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.5 }}>
            {pass
              ? <>A field of up to <strong>{pass.size_cap} golfers</strong>{pass.status === 'available' ? <>, good for one event on or before <strong>{fmtDate(pass.expires_at)}</strong></> : pass.status === 'read_only' ? <>. This event is finalized; results stay viewable until {fmtDate(pass.read_only_until)}.</> : <>, attached to your event</>}. Your golfers join free as guests.</>
              : access.unlimited ? <>Events are included in your plan. No pass needed.</> : <>Your League plan includes one event this year.</>}
          </div>
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1f2937', margin: '0 0 4px' }}>Next steps</h1>
        <p style={{ fontSize: 14.5, color: '#4b5563', margin: '0 0 16px', lineHeight: 1.5 }}>
          Work down the list in order. Steps 1 to 4 can be done weeks ahead.
        </p>

        <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'grid', gap: 10 }}>
          {STEPS.map((s, i) => {
            const isDone = !!done[s.key];
            const isNext = i === firstOpen;
            return (
              <li key={s.key} data-testid={`step-${s.key}`} data-done={isDone ? 'yes' : 'no'}
                style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 12, padding: '13px 14px', borderRadius: 12, border: isNext ? `2px solid ${green}` : '1px solid #e5e7eb', background: isDone ? '#f9fafb' : '#fff' }}>
                <span style={{ width: 30, height: 30, borderRadius: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, background: isDone ? '#dcfce7' : isNext ? green : '#f3f4f6', color: isDone ? green : isNext ? '#fff' : '#6b7280' }}>
                  {isDone ? '✓' : i + 1}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: 16, color: isDone ? '#6b7280' : '#111827' }}>{s.title}</span>
                    {isDone && <span style={{ fontSize: 12.5, fontWeight: 700, color: green }}>Done</span>}
                    {isNext && <span style={{ fontSize: 12.5, fontWeight: 700, color: green }}>Start here</span>}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '3px 0 4px', overflowWrap: 'anywhere' }}>{s.where}</div>
                  <div style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.55 }}>{s.body}</div>
                  {s.link && <a href={s.link.href} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 6, fontSize: 14, fontWeight: 700, color: green }}>{s.link.label} ↗</a>}
                </div>
              </li>
            );
          })}
        </ol>

        <div style={{ display: 'grid', gap: 10 }}>
          <a href="/dashboard" style={primaryBtn} data-testid="ready-dashboard">Go to my dashboard</a>
          <a href={GUIDE} target="_blank" rel="noopener noreferrer" style={outlineBtn}>📖 Read the full guide: Setting Up Your Tournament ↗</a>
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '14px 0 0', textAlign: 'center', lineHeight: 1.5 }}>
          Come back to this list any time with <strong>📋 Event checklist</strong> on your dashboard.
        </p>
      </div>
    </Shell>
  );
}
